import React, { useEffect, useMemo, useState } from 'react';
import { Search, Trash2, Volume2, Filter, MessageSquare } from 'lucide-react';
import api from '../services/api';
import { useModal } from '../components/AppModal';
import Pagination from '../components/Pagination';
import useUserPreference from '../hooks/useUserPreference';
import useViewMode from '../hooks/useViewMode';
import useFieldLayout from '../hooks/useFieldLayout';
import VistaControles from '../components/VistaControles';
import DataTable from '../components/DataTable';

// Módulo unificado Conversaciones+Grabaciones.
// Cargamos /api/recordings (que ya cruza archivos en disco con la fila de
// minerva_conversations correspondiente) y, además, las conversaciones de
// /api/minerva-conversations que NO tengan archivos en disco para no
// perderlas. Cada card incluye reproductor de las pistas mix/in/out y
// transcripción inline.

const formatDate = (d) => d ? new Date(d).toLocaleString('es-ES') : '—';
const formatDuration = (s) => {
  if (s == null) return '—';
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return m > 0 ? `${m}m ${sec}s` : `${sec}s`;
};
const formatSize = (b) => {
  if (b == null) return '—';
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(0)} KB`;
  return `${(b / (1024 * 1024)).toFixed(1)} MB`;
};
const formatEur = (n) => (n == null ? '—' : `${n.toFixed(2)} €`);

const SOURCE_BADGE = {
  cliente: 'badge-success',
  proveedor: 'badge-info',
  agenda: 'badge-gray',
  desconocido: 'badge-warning',
};

const Conversations = () => {
  const modal = useModal();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Set());
  const [search, setSearch] = useUserPreference('conversations:search', '');
  const [viewMode, setViewMode] = useViewMode('conversations');
  const fl = useFieldLayout('conversations', viewMode);

  const [dateFrom, setDateFrom] = useUserPreference('conversations:dateFrom', '');
  const [dateTo, setDateTo] = useUserPreference('conversations:dateTo', '');
  const [durationMinSec, setDurationMinSec] = useUserPreference('conversations:durationMinSec', '');
  const [durationMaxSec, setDurationMaxSec] = useUserPreference('conversations:durationMaxSec', '');
  const [phone, setPhone] = useUserPreference('conversations:phone', '');
  const [callerType, setCallerType] = useUserPreference('conversations:callerType', '');
  const [orphansOnly, setOrphansOnly] = useUserPreference('conversations:orphansOnly', false);
  const [withoutAudioOnly, setWithoutAudioOnly] = useUserPreference('conversations:withoutAudioOnly', false);

  const [audio, setAudio] = useState({});
  const [transcripts, setTranscripts] = useState({});

  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useUserPreference('conversations:pageSize', 25);

  // Resetear a la primera página cuando cambian los filtros que afectan
  // a la lista visible.
  useEffect(() => { setPage(1); }, [search, callerType, dateFrom, dateTo, durationMinSec, durationMaxSec, phone, orphansOnly, withoutAudioOnly, pageSize]);

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (dateFrom) params.set('dateFrom', dateFrom);
      if (dateTo) params.set('dateTo', dateTo);
      if (durationMinSec) params.set('durationMinSec', durationMinSec);
      if (durationMaxSec) params.set('durationMaxSec', durationMaxSec);
      if (phone) params.set('phone', phone);
      if (callerType) params.set('callerType', callerType);
      if (orphansOnly) params.set('orphansOnly', 'true');

      const [recRes, convRes] = await Promise.all([
        api.get(`/recordings?${params.toString()}`),
        api.get('/minerva-conversations?limit=300'),
      ]);
      const recordings = Array.isArray(recRes.data) ? recRes.data : [];
      const conversations = Array.isArray(convRes.data) ? convRes.data : [];

      const byCallSid = new Map();
      for (const r of recordings) byCallSid.set(r.callSid, r);

      // Añadir las conversaciones que NO tengan archivos para no perderlas.
      // Las normalizamos al mismo shape que /api/recordings (parts={}, sin
      // sizeBytes ni costEur).
      for (const c of conversations) {
        if (!c.callSid || byCallSid.has(c.callSid)) continue;
        byCallSid.set(c.callSid, {
          callSid: c.callSid,
          parts: {},
          sizeBytes: 0,
          fileDurationSec: null,
          conversation: c,
          resolvedName: c.resolvedName || null,
          resolvedSource: c.resolvedSource || (c.caller ? 'desconocido' : 'desconocido'),
          resolvedId: c.resolvedId || null,
          direction: 'inbound', // sin outbound info, asumimos entrante
          otherPartyNumber: c.caller || null,
          costEur: null,
          displayName: c.resolvedName || c.callerName || c.caller || c.callSid,
          effectiveDate: c.createdAt,
          _noAudio: true, // marca: existe la conversación pero no archivos
        });
      }

      const merged = Array.from(byCallSid.values()).sort((a, b) => new Date(b.effectiveDate) - new Date(a.effectiveDate));
      setItems(merged);
      setSelected(new Set());
      setAudio({});
      setTranscripts({});
    } catch (e) {
      modal.error('Error cargando conversaciones');
    } finally {
      setLoading(false);
    }
  };

  const filtered = useMemo(() => {
    let list = items;
    if (withoutAudioOnly) list = list.filter((r) => r._noAudio);
    if (!search) return list;
    const q = search.toLowerCase();
    return list.filter((r) =>
      (r.displayName || '').toLowerCase().includes(q) ||
      (r.conversation?.caller || '').toLowerCase().includes(q) ||
      (r.conversation?.callerName || '').toLowerCase().includes(q) ||
      (r.conversation?.detectedCaseKey || '').toLowerCase().includes(q) ||
      (r.conversation?.resolutionSummary || '').toLowerCase().includes(q)
    );
  }, [items, search, withoutAudioOnly]);

  const totalSize = filtered.reduce((acc, r) => acc + (r.sizeBytes || 0), 0);
  const selectedSize = filtered.filter((r) => selected.has(r.callSid)).reduce((acc, r) => acc + (r.sizeBytes || 0), 0);
  const selectableFiltered = filtered.filter((r) => !r._noAudio);
  const allSelected = selectableFiltered.length > 0 && selectableFiltered.every((r) => selected.has(r.callSid));
  const someSelected = selectableFiltered.some((r) => selected.has(r.callSid));

  const paged = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filtered.slice(start, start + pageSize);
  }, [filtered, page, pageSize]);

  const toggleAll = () => {
    if (allSelected) {
      const next = new Set(selected);
      selectableFiltered.forEach((r) => next.delete(r.callSid));
      setSelected(next);
    } else {
      const next = new Set(selected);
      selectableFiltered.forEach((r) => next.add(r.callSid));
      setSelected(next);
    }
  };

  const toggleOne = (callSid) => {
    const next = new Set(selected);
    if (next.has(callSid)) next.delete(callSid); else next.add(callSid);
    setSelected(next);
  };

  const toggleTranscripts = async (callSid) => {
    const current = transcripts[callSid];
    if (current?.open) {
      setTranscripts((t) => ({ ...t, [callSid]: { ...current, open: false } }));
      return;
    }
    if (current?.items) {
      setTranscripts((t) => ({ ...t, [callSid]: { ...current, open: true } }));
      return;
    }
    setTranscripts((t) => ({ ...t, [callSid]: { open: true, loading: true, items: null } }));
    try {
      const res = await api.get(`/minerva-conversations/${encodeURIComponent(callSid)}/transcripts`);
      const list = Array.isArray(res.data) ? res.data : [];
      setTranscripts((t) => ({ ...t, [callSid]: { open: true, loading: false, items: list } }));
    } catch (e) {
      modal.error('Error cargando transcripción');
      setTranscripts((t) => ({ ...t, [callSid]: { open: false, loading: false, items: null } }));
    }
  };

  const playPart = async (callSid, part) => {
    setAudio((a) => ({ ...a, [callSid]: { part, src: null } }));
    try {
      const res = await api.get(`/minerva-conversations/${encodeURIComponent(callSid)}/audio?part=${part}`, { responseType: 'blob' });
      setAudio((a) => ({ ...a, [callSid]: { part, src: URL.createObjectURL(res.data) } }));
    } catch (e) {
      modal.error('Error cargando audio');
      setAudio((a) => { const next = { ...a }; delete next[callSid]; return next; });
    }
  };

  const doDelete = async () => {
    setDeleting(true);
    try {
      const callSids = Array.from(selected);
      const res = await api.post('/recordings/delete', { callSids });
      modal.success(`Borradas ${res.data.deletedFiles} archivo(s) · liberados ${formatSize(res.data.bytesFreed)}`);
      setConfirmDelete(false);
      await load();
    } catch (e) {
      modal.error(e.response?.data?.message || 'Error borrando');
    } finally {
      setDeleting(false);
    }
  };

  const clearFilters = () => {
    setDateFrom(''); setDateTo(''); setDurationMinSec(''); setDurationMaxSec('');
    setPhone(''); setCallerType(''); setOrphansOnly(false); setWithoutAudioOnly(false);
  };

  const columns = [
    { key: 'select', header: '', render: (r) => {
      const availableParts = ['mix', 'in', 'out'].filter((p) => r.parts && r.parts[p]);
      if (availableParts.length === 0) return null;
      return (
        <input
          type="checkbox"
          checked={selected.has(r.callSid)}
          onChange={() => toggleOne(r.callSid)}
          title="Seleccionar grabación"
        />
      );
    } },
    { key: 'conversation', header: 'Conversación', className: 'dt-strong', fixed: true, render: (r) => {
      const conv = r.conversation;
      const cardNumber = conv?.id ? `#${conv.id}` : null;
      const otherPartyName = r.resolvedName || conv?.callerName || r.otherPartyNumber || null;
      const otherLabel = otherPartyName || 'desconocido';
      const callerName = r.direction === 'outbound' ? 'Minerva' : otherLabel;
      const receiverName = r.direction === 'outbound' ? otherLabel : 'Minerva';
      const titlePrefix = cardNumber ? `${cardNumber} · ` : (conv ? 'Conv. · ' : null);
      if (titlePrefix === null) return 'Sin nº';
      return (
        <span title={r.callSid}>
          {titlePrefix}
          <span style={{ color: '#1e3a8a' }}>{callerName}</span>
          <span> → </span>
          <span style={{ color: '#14532d' }}>{receiverName}</span>
        </span>
      );
    } },
    { key: 'date', header: 'Fecha', className: 'dt-nowrap', render: (r) => formatDate(r.effectiveDate) },
    { key: 'phone', header: 'Teléfono', className: 'dt-nowrap', render: (r) => r.otherPartyNumber || '—' },
    { key: 'source', header: 'Tipo', render: (r) => (
      ['cliente', 'proveedor', 'agenda'].includes(r.resolvedSource)
        ? <span className={`badge ${SOURCE_BADGE[r.resolvedSource]}`}>{r.resolvedSource}</span>
        : '—'
    ) },
    { key: 'duration', header: 'Duración', className: 'num', render: (r) => formatDuration(r.conversation?.durationSec ?? r.fileDurationSec ?? null) },
    { key: 'cost', header: 'Coste', className: 'num', render: (r) => formatEur(r.costEur) },
    { key: 'size', header: 'Tamaño', className: 'num', render: (r) => (r.sizeBytes > 0 ? formatSize(r.sizeBytes) : '—') },
    { key: 'case', header: 'Caso', render: (r) => r.conversation?.detectedCaseKey || '—' },
    { key: 'summary', header: 'Resumen', render: (r) => (
      r.conversation?.resolutionSummary
        ? <span className="dt-sub">{r.conversation.resolutionSummary}</span>
        : '—'
    ) },
    // Las mismas acciones que la tarjeta: escuchar cada pista y ver la
    // transcripción. Los botones no desaparecen: el que no aplica (pista sin
    // grabar, audio huérfano sin conversación…) se queda en su sitio, apagado.
    { key: 'actions', header: '', className: 'col-actions', render: (r) => {
      const a = audio[r.callSid];
      const tr = transcripts[r.callSid];
      const conSesion = Boolean(r.conversation) || ['mix', 'in', 'out'].some((p) => r.parts && r.parts[p]);
      return (
        <div className="dt-actions">
          {['mix', 'in', 'out'].map((p) => (
            <button
              key={p}
              type="button"
              className={`btn btn-sm ${a?.part === p ? 'btn-primary' : 'btn-outline'}`}
              style={{ padding: '2px 8px', fontSize: 11 }}
              disabled={!(r.parts && r.parts[p])}
              onClick={() => playPart(r.callSid, p)}
              title={r.parts && r.parts[p] ? 'Escuchar' : 'Sin grabación de esta pista'}
            >
              {p === 'mix' ? 'Mezcla' : p === 'in' ? 'Llamante' : 'Bot'}
            </button>
          ))}
          <button
            type="button"
            className={`btn btn-sm ${tr?.open ? 'btn-primary' : 'btn-outline'}`}
            style={{ padding: '2px 8px', fontSize: 11 }}
            disabled={!conSesion}
            onClick={() => toggleTranscripts(r.callSid)}
          >
            {tr?.open ? 'Ocultar' : 'Ver transcripción'}
          </button>
        </div>
      );
    } },
    // Reproductor y transcripción, en la segunda fila de esa conversación y solo
    // cuando se han abierto (sin nada abierto, la fila no se pinta).
    { key: 'detalle', header: '', render: (r) => {
      const a = audio[r.callSid];
      const tr = transcripts[r.callSid];
      if (!a && !tr?.open) return null;
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, width: '100%' }}>
          {a && (a.src ? (
            <audio controls src={a.src} style={{ width: '100%' }} />
          ) : (
            <div style={{ fontSize: 11, color: '#94a3b8' }}>Cargando…</div>
          ))}
          {tr?.open && (tr.loading ? (
            <div style={{ fontSize: 11, color: '#94a3b8' }}>Cargando…</div>
          ) : !tr.items || tr.items.length === 0 ? (
            <div style={{ fontSize: 11, color: '#94a3b8', fontStyle: 'italic' }}>Sin transcripción.</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, width: '100%' }}>
              {tr.items.map((t) => (
                <div key={t.id} style={{
                  background: t.direction === 'caller' ? '#eef2ff' : '#f0fdf4',
                  borderRadius: 6, padding: '6px 8px', fontSize: 12,
                }}>
                  <div style={{ fontSize: 10, color: '#64748b', marginBottom: 2 }}>
                    {t.direction === 'caller' ? 'Llamante' : 'Minerva'}
                  </div>
                  <div>{t.content}</div>
                </div>
              ))}
            </div>
          ))}
        </div>
      );
    } },
  ];

  const fields = fl.fields(columns);

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Conversaciones</h1>
          <p className="page-subtitle">
            {filtered.length} conversaciones · {formatSize(totalSize)}
            {selected.size > 0 && ` · ${selected.size} grabaciones marcadas (${formatSize(selectedSize)})`}
          </p>
        </div>
        {selected.size > 0 && (
          <button className="btn btn-danger" onClick={() => setConfirmDelete(true)}>
            <Trash2 size={18} /> Borrar grabaciones ({selected.size})
          </button>
        )}
      </div>

      <div className="card" style={{ marginBottom: 16 }}>
        <h3 style={{ margin: '0 0 12px 0', fontSize: 14, display: 'flex', alignItems: 'center', gap: 8 }}>
          <Filter size={16} /> Filtros
        </h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12 }}>
          <div className="form-group" style={{ margin: 0 }}>
            <label className="form-label">Desde</label>
            <input type="date" className="form-input" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
          </div>
          <div className="form-group" style={{ margin: 0 }}>
            <label className="form-label">Hasta</label>
            <input type="date" className="form-input" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
          </div>
          <div className="form-group" style={{ margin: 0 }}>
            <label className="form-label">Duración mínima (seg)</label>
            <input type="number" min="0" className="form-input" value={durationMinSec} onChange={(e) => setDurationMinSec(e.target.value)} placeholder="ej. 5" />
          </div>
          <div className="form-group" style={{ margin: 0 }}>
            <label className="form-label">Duración máxima (seg)</label>
            <input type="number" min="0" className="form-input" value={durationMaxSec} onChange={(e) => setDurationMaxSec(e.target.value)} placeholder="ej. 120" />
          </div>
          <div className="form-group" style={{ margin: 0 }}>
            <label className="form-label">Teléfono contiene</label>
            <input type="text" className="form-input" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="ej. 600" />
          </div>
          <div className="form-group" style={{ margin: 0 }}>
            <label className="form-label">Tipo de llamante</label>
            <select className="form-input" value={callerType} onChange={(e) => setCallerType(e.target.value)}>
              <option value="">Todos</option>
              <option value="cliente">Solo clientes</option>
              <option value="proveedor">Solo proveedores</option>
              <option value="agenda">Solo agendados</option>
              <option value="desconocido">Solo desconocidos</option>
            </select>
          </div>
          <div className="form-group" style={{ margin: 0, display: 'flex', flexDirection: 'column', gap: 4 }}>
            <label className="form-label">Estado audio</label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#475569' }}>
              <input type="checkbox" checked={orphansOnly} onChange={(e) => setOrphansOnly(e.target.checked)} />
              Solo audio huérfano
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#475569' }}>
              <input type="checkbox" checked={withoutAudioOnly} onChange={(e) => setWithoutAudioOnly(e.target.checked)} />
              Solo conversación sin audio
            </label>
          </div>
          <div className="form-group" style={{ margin: 0, display: 'flex', alignItems: 'flex-end', gap: 8 }}>
            <button className="btn btn-primary" onClick={load} style={{ flex: 1 }}>Aplicar</button>
            <button className="btn btn-outline" onClick={() => { clearFilters(); }}>Limpiar</button>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="search-bar" style={{ alignItems: 'center' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginRight: 12, whiteSpace: 'nowrap' }}>
            <input
              type="checkbox"
              checked={allSelected}
              ref={(el) => { if (el) el.indeterminate = !allSelected && someSelected; }}
              onChange={toggleAll}
            />
            <span style={{ fontSize: 13, color: '#475569' }}>{allSelected ? 'Desmarcar todo' : 'Marcar todo'}</span>
          </label>
          <div className="search-input-wrapper">
            <Search />
            <input className="form-input" placeholder="Buscar por nombre, teléfono, caso, resumen…" value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>
          <div style={{ marginLeft: 'auto' }}>
            <VistaControles
              viewMode={viewMode}
              onViewModeChange={setViewMode}
              fields={fields}
              hidden={fl.hidden}
              onToggleField={fl.toggle}
              onReorderFields={fl.reorder}
            />
          </div>
        </div>

        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#64748b' }}>Cargando...</div>
        ) : filtered.length === 0 ? (
          <div className="empty-state">
            <MessageSquare size={64} />
            <h3>No hay conversaciones</h3>
            <p>Ajusta los filtros o espera a que entren llamadas.</p>
          </div>
        ) : (
          <>
          <Pagination total={filtered.length} page={page} pageSize={pageSize} onPage={setPage} onPageSize={setPageSize} />
          {viewMode === 'table' ? (
            <DataTable columns={fl.orderColumns(columns)} rows={paged} rowKey={(r) => r.callSid} hiddenKeys={fl.hidden} segundaFila={['detalle']} />
          ) : (
          <div className="items-grid">
            {paged.map((r) => {
              const isChecked = selected.has(r.callSid);
              const conv = r.conversation;
              const a = audio[r.callSid];
              const tr = transcripts[r.callSid];
              const availableParts = ['mix', 'in', 'out'].filter((p) => r.parts && r.parts[p]);
              const hasAudio = availableParts.length > 0;
              const cardNumber = conv?.id ? `#${conv.id}` : null;
              const otherPartyName = r.resolvedName || conv?.callerName || r.otherPartyNumber || null;
              const otherLabel = otherPartyName || 'desconocido';
              const callerName = r.direction === 'outbound' ? 'Minerva' : otherLabel;
              const receiverName = r.direction === 'outbound' ? otherLabel : 'Minerva';
              const titlePrefix = cardNumber ? `${cardNumber} · ` : (conv ? 'Conv. · ' : null);
              const showSourceBadge = ['cliente', 'proveedor', 'agenda'].includes(r.resolvedSource);
              const CALLER_COLOR = '#1e3a8a';
              const RECEIVER_COLOR = '#14532d';
              const durationSec = conv?.durationSec ?? r.fileDurationSec ?? null;
              return (
                <div className="item-card" key={r.callSid} style={{ borderLeft: isChecked ? '3px solid #1d4ed8' : (r._noAudio ? '3px solid #cbd5e1' : undefined) }}>
                  <div className="item-card-header">
                    {hasAudio ? (
                      <input
                        type="checkbox"
                        checked={isChecked}
                        onChange={() => toggleOne(r.callSid)}
                        style={{ width: 18, height: 18, cursor: 'pointer' }}
                      />
                    ) : (
                      <span style={{ width: 18 }} />
                    )}
                    <div className="item-card-avatar">{(otherLabel || '?').charAt(0).toUpperCase()}</div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div className="item-card-title" title={r.callSid} style={{ wordBreak: 'break-word' }}>
                        {titlePrefix === null ? (
                          'Sin nº'
                        ) : (
                          <>
                            {titlePrefix}
                            <span style={{ color: CALLER_COLOR }}>{callerName}</span>
                            <span> → </span>
                            <span style={{ color: RECEIVER_COLOR }}>{receiverName}</span>
                          </>
                        )}
                      </div>
                      {!fl.isHidden('date') && <div className="item-card-subtitle">{formatDate(r.effectiveDate)}</div>}
                    </div>
                    {!fl.isHidden('source') && showSourceBadge && (
                      <span className={`badge ${SOURCE_BADGE[r.resolvedSource]}`}>{r.resolvedSource}</span>
                    )}
                  </div>
                  <div className="item-card-body">
                    {!fl.isHidden('phone') && r.otherPartyNumber && (
                      <div className="item-card-row" style={fl.cardStyle('phone')}><span className="item-label">Tel</span><span>{r.otherPartyNumber}</span></div>
                    )}
                    {!fl.isHidden('duration') && <div className="item-card-row" style={fl.cardStyle('duration')}><span className="item-label">Duración</span><span>{formatDuration(durationSec)}</span></div>}
                    {!fl.isHidden('cost') && r.direction === 'outbound' && (
                      <div className="item-card-row" style={fl.cardStyle('cost')}><span className="item-label">Coste</span><span>{formatEur(r.costEur)}</span></div>
                    )}
                    {!fl.isHidden('size') && r.sizeBytes > 0 && (
                      <div className="item-card-row" style={fl.cardStyle('size')}><span className="item-label">Tamaño audio</span><span>{formatSize(r.sizeBytes)}</span></div>
                    )}
                    {!fl.isHidden('case') && conv?.detectedCaseKey && <div className="item-card-row" style={fl.cardStyle('case')}><span className="item-label">Caso</span><span>{conv.detectedCaseKey}</span></div>}
                    {!fl.isHidden('summary') && conv?.resolutionSummary && <div className="item-card-row" style={{ ...fl.cardStyle('summary'), flexDirection: 'column', alignItems: 'flex-start', gap: 4 }}><span className="item-label">Resumen</span><span style={{ fontSize: 12, color: '#475569' }}>{conv.resolutionSummary}</span></div>}
                    {!conv && <div className="item-card-row"><span className="item-label">Conversación</span><span style={{ color: '#94a3b8', fontStyle: 'italic' }}>Audio huérfano (sin fila en BD)</span></div>}
                    {conv && r._noAudio && <div className="item-card-row"><span className="item-label">Audio</span><span style={{ color: '#94a3b8', fontStyle: 'italic' }}>Sin grabación</span></div>}
                    {hasAudio && (
                      <div className="item-card-row" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: 6 }}>
                        <span className="item-label" style={{ display: 'flex', alignItems: 'center', gap: 4 }}><Volume2 size={12} /> Audio</span>
                        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                          {availableParts.map((p) => (
                            <button
                              key={p}
                              type="button"
                              className={`btn btn-sm ${a?.part === p ? 'btn-primary' : 'btn-outline'}`}
                              style={{ padding: '2px 8px', fontSize: 11 }}
                              onClick={() => playPart(r.callSid, p)}
                            >
                              {p === 'mix' ? 'Mezcla' : p === 'in' ? 'Llamante' : 'Bot'}
                            </button>
                          ))}
                        </div>
                        {a && (a.src ? (
                          <audio controls src={a.src} style={{ width: '100%', marginTop: 4 }} />
                        ) : (
                          <div style={{ fontSize: 11, color: '#94a3b8' }}>Cargando…</div>
                        ))}
                      </div>
                    )}
                    {(conv || hasAudio) && (
                      <div className="item-card-row" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: 6 }}>
                        <span className="item-label" style={{ display: 'flex', alignItems: 'center', gap: 4 }}><MessageSquare size={12} /> Transcripción</span>
                        <button
                          type="button"
                          className={`btn btn-sm ${tr?.open ? 'btn-primary' : 'btn-outline'}`}
                          style={{ padding: '2px 8px', fontSize: 11 }}
                          onClick={() => toggleTranscripts(r.callSid)}
                        >
                          {tr?.open ? 'Ocultar' : 'Ver transcripción'}
                        </button>
                        {tr?.open && (tr.loading ? (
                          <div style={{ fontSize: 11, color: '#94a3b8' }}>Cargando…</div>
                        ) : !tr.items || tr.items.length === 0 ? (
                          <div style={{ fontSize: 11, color: '#94a3b8', fontStyle: 'italic' }}>Sin transcripción.</div>
                        ) : (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, width: '100%' }}>
                            {tr.items.map((t) => (
                              <div key={t.id} style={{
                                background: t.direction === 'caller' ? '#eef2ff' : '#f0fdf4',
                                borderRadius: 6, padding: '6px 8px', fontSize: 12,
                              }}>
                                <div style={{ fontSize: 10, color: '#64748b', marginBottom: 2 }}>
                                  {t.direction === 'caller' ? 'Llamante' : 'Minerva'}
                                </div>
                                <div>{t.content}</div>
                              </div>
                            ))}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          )}
          <Pagination total={filtered.length} page={page} pageSize={pageSize} onPage={setPage} onPageSize={setPageSize} />
          </>
        )}
      </div>

      {confirmDelete && (
        <div className="modal-overlay" onClick={() => !deleting && setConfirmDelete(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Borrar grabaciones</h2>
              <button className="btn-icon" onClick={() => !deleting && setConfirmDelete(false)}>&times;</button>
            </div>
            <div className="modal-body">
              <p>Vas a borrar los archivos de audio de <strong>{selected.size}</strong> conversaciones (mezcla, llamante, bot, eventos y transcripción).</p>
              <p>La fila de la conversación en BD se conserva.</p>
              <p>Espacio que se liberará: <strong>{formatSize(selectedSize)}</strong>.</p>
              <p style={{ color: '#dc2626' }}>Esta acción no se puede deshacer.</p>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setConfirmDelete(false)} disabled={deleting}>Cancelar</button>
              <button className="btn btn-danger" onClick={doDelete} disabled={deleting}>
                {deleting ? 'Borrando…' : 'Borrar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Conversations;
